/**
 * File: DashboardPage.tsx
 * Path: apps/admin/src/routes/dashboard/DashboardPage.tsx
 * Description: Main dashboard orchestration page that queries GA4 API data proxies through the regionalized Firebase Functions instance (FUNCTIONS_REGION).
 */

import { TAB_LIST_CLASS, TAB_TRIGGER_CLASS } from "@/modules/layout/utils/adminTabsData";
import React from "react";
import { AnalyticsChart } from "@/features/dashboard/components/AnalyticsChart";
import { DateRangePicker } from "@/features/dashboard/components/DateRangePicker";
import { useAnalyticsDashboardController } from "@/features/dashboard/hooks/useAnalyticsDashboardController";
import { useDashboardPageController } from "@/features/dashboard/hooks/useDashboardPageController";
import { useRoleHomeController } from "@/features/dashboard/hooks/useRoleHomeController";
import { useSalesHomeController } from "@/features/dashboard/hooks/useSalesHomeController";
import { PageHeader } from "@/modules/layout/components/PageHeader";
import { StatCard } from "@/shared/components/display/StatCard";
import { ROLE_DESCRIPTIONS, ROLE_LABELS } from "@idk/types";
import { Link } from "react-router-dom";
import { TodayPage } from "@/routes/today/TodayPage";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/Tabs";

/**
 * Website traffic from GA4, for roles with the `analytics` permission.
 */
const AnalyticsDashboard: React.FC<{ tabs?: React.ReactNode }> = ({ tabs }) => {
  const { dateRange, setDateRange, isLoading, error, metrics, chartData } = useAnalyticsDashboardController();

  return (
    <div className="space-y-stack-xl">
      <PageHeader
        title="Website Analytics"
        description="Audience engagement insights and tool funnel execution benchmarks."
      >
        <DateRangePicker
          value={dateRange}
          onChange={setDateRange}
          disabled={isLoading}
        />
      </PageHeader>
      {tabs}

      {error && (
        <div className="p-gutter-sm bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-none">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-stack-lg">
        <StatCard
          label="Active Users"
          value={metrics.activeUsers.toLocaleString()}
          isLoading={isLoading}
        />
        <StatCard
          label="Total Sessions"
          value={metrics.sessions.toLocaleString()}
          isLoading={isLoading}
        />
        <StatCard
          label="Engagement Rate"
          value={`${(metrics.engagementRate * 100).toFixed(1)}%`}
          isLoading={isLoading}
        />
      </div>

      <div className="space-y-stack-sm">
        <h2 className="text-lg font-semibold tracking-tight">
          User Traffic Trend
        </h2>
        <AnalyticsChart data={chartData} height={250} isLoading={isLoading} />
      </div>
    </div>
  );
};

/**
 * Roles without analytics (editor, sales) get their own start page instead of
 * a traffic panel the server would refuse to fill.
 */
const RoleHome: React.FC = () => {
  const { role, links } = useRoleHomeController();

  return (
    <div className="space-y-stack-xl">
      <PageHeader
        title="Dashboard"
        description={
          role ? `${ROLE_LABELS[role]}: ${ROLE_DESCRIPTIONS[role]}` : undefined
        }
      />

      <nav aria-label="Your pages" className="max-w-prose-wide">
        <ul className="divide-y divide-border/40 border-y border-border/40">
          {links.map((item) => (
            <li key={item.href}>
              <Link
                to={item.href!}
                className="flex min-h-11 items-center text-sm font-medium hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};


/**
 * Sales staff land on Today. Anyone who also has analytics gets a Website
 * tab for the traffic panel (?tab=website).
 */
const SalesHome: React.FC = () => {
  const { showWebsite, tab, changeTab } = useSalesHomeController();

  if (!showWebsite) return <TodayPage />;
  // The title comes first; each page draws these tabs right under its own title.
  const tabList = (
    <TabsList className={TAB_LIST_CLASS}>
      <TabsTrigger value="today" className={TAB_TRIGGER_CLASS}>
        Business
      </TabsTrigger>
      <TabsTrigger value="website" className={TAB_TRIGGER_CLASS}>
        Website
      </TabsTrigger>
    </TabsList>
  );
  return (
    <Tabs value={tab} onValueChange={changeTab} className="w-full space-y-stack-xl">
      <TabsContent value="today" className="mt-0 outline-none">
        <TodayPage tabs={tabList} />
      </TabsContent>
      <TabsContent value="website" className="mt-0 outline-none">
        <AnalyticsDashboard tabs={tabList} />
      </TabsContent>
    </Tabs>
  );
};

export const DashboardPage: React.FC = () => {
  const { view } = useDashboardPageController();
  if (view === "sales") return <SalesHome />;
  return view === "analytics" ? <AnalyticsDashboard /> : <RoleHome />;
};
